import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import {eventPrevented} from '../../../../utils/eventPrevented';
import {Column} from '../../../../stores/Form/Column';
import {keys} from '../../../../constants/KeyCodes';
import {FormInputButton} from '../FormInputButton';
import {
    chooseImageByZoomType,
    fieldValueToEmail,
    fieldValueToUrl
} from '../../utils/formInputButton';
import {fieldValue} from '../../../../stores/Form/Field';
import {isOperatorField} from '../../utils/filters';
import {CellSearchButton, OpenSearchDropdownAPI} from '../CellSearchButton';
import {isZoomable, zoom} from '../../actions/zoom';
import {listTypes} from '../SelectListModal/enum';
import {isDate} from '../../utils/date';
import {showFormFieldHelp} from '../../utils/help';
import {SuspendedJobTypes} from '../../consts/suspendedJobTypes';
import {FormInputComponent} from '../Input';
import {getOperatorsSearchObject} from './constants';
import {getHTMLCellID} from '../../actions/utils/getHTMLcellID';
import {formsStore} from '../../../../stores/Form/FormsStore';
import {lockScreenKeyboardNavigation} from '../../actions/lockScreenKeyboardNavigation';
import {AttachmentType} from '../../../../components/FileUploader';
import {createPriorityFileObj} from '../SidePanelForms/Attachments/utils';
import {fieldUpdate} from '../../actions/standAloneForm';
import {mapValueByColumnType} from '../../../../stores/Form/utils';
import {defaultSidePanelOptions} from '../SidePanelForms/Common/consts';
import style from '../../styles/formInput.module.scss';
import {preDownload} from '../SidePanelForms/Attachments/actions';
import {parsePathToFileName, previewFile} from '../../utils/file-processing';

interface FormInputProps {
    initValue: fieldValue;
    origValue: fieldValue;
    column: Column;
    rowIndex: number;
    searchMode: boolean;
    isActive: boolean;
    updateSearchValue: (val: string, colId: number, rowIndex: number) => void;
    updateCurrentValue: (val: any) => void;
    onMaxWidthMode?: () => void;
    onFocus?: (el) => void;
    openSelectListModal: (listType: listTypes) => void;
    getCellWrap?: () => HTMLDivElement | null;
    componentClasses?: string;
    viewMode?: 'table' | 'form';
    themeStyle?: 'light' | 'dark';
}

export const FormInput = memo((props: FormInputProps) => {
    const {
        column,
        rowIndex,
        searchMode,
        initValue,
        isActive,
        updateSearchValue,
        onMaxWidthMode,
        updateCurrentValue,
        onFocus,
        getCellWrap,
        openSelectListModal,
        componentClasses,
        viewMode = 'form',
        themeStyle
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const f6Timer = useRef(null);
    const fieldSearchRef = useRef<OpenSearchDropdownAPI>(null);

    const [value, setValue] = useState(initValue);

    const setMaxWidthMode = useCallback(
        (flag: boolean) => {
            column.setMaxWidthMode(flag);
            if (onMaxWidthMode) onMaxWidthMode();
        },
        [onMaxWidthMode, column]
    );

    useEffect(() => {
        if (isActive) {
            inputRef.current?.focus({preventScroll: true});
            if (!column.editMode) {
                inputRef.current?.select?.();
            }
        }
    }, [isActive, column]);

    const setEditMode = editMode => {
        column.setEditMode(editMode);
        if (editMode && !column.maxWidthMode) {
            setMaxWidthMode(true);
        }
    };

    const toggleEditMode = () => {
        setEditMode(!column.editMode);
    };

    const onDropdownClose = useCallback(() => {
        lockScreenKeyboardNavigation(false);
        inputRef.current?.focus();
    }, [column.parentForm.session]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const {key} = event;
        const {ZoomType} = priority.netitems.form;
        const zoomType = getZoomType(column);
        switch (key) {
            case keys.F1:
                if (!event.ctrlKey && !event.metaKey) {
                    showFormFieldHelp({
                        anchor: getCellWrap(),
                        column
                    });
                }
                break;
            case keys.F2:
                inputRef.current.setSelectionRange(-1, -1);
                toggleEditMode();
                setMaxWidthMode(true);
                break;
            case keys.F6:
                if (zoomType === ZoomType.ZoomZoom) {
                    zoom(column.parentForm, column);
                } else if (zoomType === ZoomType.SelectZoom) {
                    if (f6Timer.current && isZoomable(column)) {
                        f6Timer.current = clearTimeout(f6Timer.current);
                        zoom(column.parentForm, column);
                        return;
                    }
                    const isForceSearch = event.ctrlKey;
                    f6Timer.current = setTimeout(() => {
                        //here we'll open the searchDropdown
                        const fieldSearchAPI = fieldSearchRef.current;
                        if (fieldSearchAPI) {
                            if (!fieldSearchAPI.isOpen) {
                                fieldSearchAPI.openSearchDropdown(isForceSearch);
                                lockScreenKeyboardNavigation(true);
                            }
                        }
                        f6Timer.current = clearTimeout(f6Timer.current);
                    }, 500);
                } else if (zoomType !== ZoomType.NoneZoom) {
                    processNonSearchZooms(zoomType);
                }
                break;
            case keys.SHIFT:
                setMaxWidthMode(true);
                break;
            case keys.SPACE:
                if (column.isReadonly) {
                    event.preventDefault();
                }
                if (zoomType === ZoomType.SelectZoom) {
                    const fieldSearchAPI = fieldSearchRef.current;
                    if (fieldSearchAPI) {
                        if (!fieldSearchAPI.isOpen && value === '') {
                            eventPrevented(event, true);
                            fieldSearchAPI.openSearchDropdown();
                        }
                    }
                } else if (zoomType !== ZoomType.NoneZoom && zoomType !== ZoomType.ZoomZoom) {
                    processNonSearchZooms(zoomType);
                }
                break;
            case keys.HOME:
                if (column.editMode) {
                    eventPrevented(event, true);
                    inputRef.current.setSelectionRange(0, 0);
                }
                break;
            case keys.END:
                if (column.editMode) {
                    eventPrevented(event, true);
                    const endPosition = inputRef.current?.value.length || 0;
                    inputRef.current.setSelectionRange(endPosition, endPosition);
                }
                break;
            default:
                break;
        }
        // processEventCodes(event.nativeEvent);
    };

    const showFormInputButton = (): boolean => {
        return column.Zoom !== priority.netitems.form.ZoomType.NoneZoom && isZoomable(column);
    };

    const getZoomType = (column: Column): priority.netitems.form.ZoomType => {
        return column.Relcol ? column.relatedIdColumn.Zoom : column.Zoom;
    };

    const openFileExplorer = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    }, []);

    const uploadFile = useCallback(
        async (file: File) => {
            try {
                const attachmentObj: AttachmentType = await createPriorityFileObj(file);

                await fieldUpdate(
                    rowIndex,
                    mapValueByColumnType(attachmentObj, column),
                    defaultSidePanelOptions
                );
            } catch (error) {
                console.error('Error processing file:', error);
            }
        },
        [column, rowIndex]
    );

    const onFileInputChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            const files = e.target.files;
            if (files && files.length > 0) {
                await uploadFile(files[0]);
            }
        },
        [uploadFile]
    );

    const onInputButtonClick = (column: Column) => {
        const zoomType = getZoomType(column);
        const {ZoomType} = priority.netitems.form;
        if (zoomType === ZoomType.SelectZoom || zoomType === ZoomType.ZoomZoom) {
            if (!column.Zoomfname && !column.Relcol && !value) {
                openSelectListModal(listTypes.DirectActivationsF);
            } else {
                formsStore.isRecording &&
                    formsStore.setEventForRecordingMacro({
                        type: SuspendedJobTypes.Zoom,
                        column: column
                    });
                zoom(column.parentForm, column);
            }
        } else {
            processNonSearchZooms(zoomType);
        }
    };

    const handleAttachmentClick = useCallback(async () => {
        const curRecord = formsStore.currentScreen.getActiveForm().records[rowIndex];
        if (curRecord?.isNew) {
            openFileExplorer();
        } else {
            await preDownload(curRecord);
            const fileData = curRecord.getField(column.id)?.Value;
            previewFile(
                fileData,
                parsePathToFileName(curRecord.getField(column.id)?.origValue as string)
            );
        }
    }, [rowIndex, openFileExplorer, column]);

    const processNonSearchZooms = async zoomType => {
        const {ZoomType} = priority.netitems.form;
        if (zoomType === ZoomType.URLZoom && value) {
            window.open(fieldValueToUrl(value));
        } else if (zoomType === ZoomType.EMailZoom) {
            window.open(fieldValueToEmail(value));
        } else if (zoomType === ZoomType.AttachZoom) {
            handleAttachmentClick();
        }
    };

    const handleChange = useCallback(
        (value: any) => {
            if (searchMode) {
                updateSearchValue(value, column.id, rowIndex);
            } else {
                if (updateCurrentValue) {
                    updateCurrentValue(value);
                }
            }
            setValue(value);
        },
        [column, updateSearchValue, searchMode, rowIndex, updateCurrentValue]
    );

    const setActiveEl = useCallback(
        e => {
            onFocus(e.currentTarget);
        },
        [onFocus]
    );

    const onSelect = useCallback(
        async (val: string) => {
            const relatedColumn = column.relatedIdColumn;
            if (relatedColumn) {
                await formsStore.currentScreen.updateRelatedField(relatedColumn.id, val);
                formsStore.setEventForRecordingMacro({
                    type: SuspendedJobTypes.FieldValue,
                    value: val
                });
            } else {
                handleChange(val);
            }
        },
        [handleChange, column]
    );

    if (!column.hidden) {
        const zoomType = getZoomType(column);
        const isBoolean = column.Type === priority.netitems.form.ColumnType.Boolean;

        const hasCellButtonSearch =
            (!column.isReadonly &&
                !isBoolean &&
                column.Zoom === priority.netitems.form.ZoomType.SelectZoom) ||
            isOperatorField(searchMode, rowIndex);

        const hasFormInputButton = showFormInputButton() && !isDate(column.Type) && !isBoolean;

        return (
            <div
                id={getHTMLCellID(column.parentForm, rowIndex, column.index)}
                className={cx(style.inputWrapper, {
                    [style.disabledInput]: column.isReadonly || column.parentForm.isReadonly,
                    [style.searchInput]: searchMode && isActive,
                    [style.active]: isActive,
                    [style.booleanInput]: isBoolean && !isOperatorField(searchMode, rowIndex),
                    [style.tableInput]: viewMode === 'table',
                    [style.formInput]: viewMode === 'form',
                    [style.hasButtons]: hasCellButtonSearch || hasFormInputButton,
                    [componentClasses]: !!componentClasses
                })}
            >
                <FormInputComponent
                    column={column}
                    searchMode={searchMode}
                    rowIndex={rowIndex}
                    value={value}
                    handleKeyDown={handleKeyDown}
                    handleChange={handleChange}
                    inputRef={inputRef}
                    isActive={isActive}
                    setMaxWidthMode={setMaxWidthMode}
                    setEditMode={setEditMode}
                    onFocus={setActiveEl}
                    themeStyle={themeStyle}
                />

                {hasCellButtonSearch && isActive && (
                    <CellSearchButton
                        ref={fieldSearchRef}
                        onClose={onDropdownClose}
                        onSelect={onSelect}
                        getCellWrap={getCellWrap}
                        column={column}
                        searchValue={value as string}
                        zindex={10010}
                        dropdownInputType="grid"
                        prefetchedResults={
                            isOperatorField(searchMode, rowIndex) && getOperatorsSearchObject()
                        }
                    />
                )}

                {hasFormInputButton && isActive && (
                    <FormInputButton
                        isPrimary={isActive}
                        icon={chooseImageByZoomType(zoomType, column.isReadonly)}
                        onClick={() => onInputButtonClick(column)}
                    />
                )}

                {zoomType === priority.netitems.form.ZoomType.AttachZoom && (
                    <input
                        ref={fileInputRef}
                        type="file"
                        style={{display: 'none'}}
                        onChange={onFileInputChange}
                    />
                )}
            </div>
        );
    }
    return null;
});
